import { AccessTime } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { ReactNode } from "react";
import { TDateISO } from "../../../utils/time/interface";
import { EMode } from "../../shared/components/features/event-scheduler/enums/event.enums";
import { ArrivalDateTimeCard } from "./Card";

export enum EArrivalTimeType {
  EXACT = "exact",
  WINDOW = "window",
}

export interface IArrivalDateTimeProps {
  value: TDateISO[];
  onChange: (value: TDateISO[]) => void;
  type: EArrivalTimeType;
  headerAction?: ReactNode;
  handleRepetitiveStartDateChange?: (value: Date | null) => void;
  isDisabled: boolean;
  dateEnabled?: boolean;
  multiplesDateTimes?: boolean;
  mode: EMode;
  minuteStep?: number;
}

export const ArrivalDateTimeComponent = ({
  value,
  onChange,
  type,
  headerAction,
  isDisabled,
  dateEnabled = true,
  multiplesDateTimes = true,
  mode,
  handleRepetitiveStartDateChange,
  minuteStep,
}: IArrivalDateTimeProps) => {
  const isWindow = type === EArrivalTimeType.WINDOW;
  const label = isWindow ? "Arrival windows" : "Exact arrival options";
  const noArrivalDateTimeAddedLabel = isWindow ? "No arrival windows added yet" : "No arrival options added yet";
  const description = {
    [EArrivalTimeType.EXACT]: "Add one or more alternative arrival times. One of these options will be chosen to schedule the visit.",
    [EArrivalTimeType.WINDOW]: "The worker can arrive anytime within the window (this is not service duration).",
  };

  const getWindows = () => {
    if (!isWindow) return [];
    const windows = [];
    for (let i = 0; i < value.length; i += 2) {
      windows.push({
        start: value[i],
        end: value[i + 1],
        index: i / 2,
      });
    }
    return windows;
  };

  const handleDelete = (startIso: string) => {
    if (isWindow) {
      const startIndex = value.indexOf(startIso as TDateISO);
      if (startIndex !== -1) {
        const newValue = value.filter((_, idx) => idx !== startIndex && idx !== startIndex + 1);
        onChange(newValue);
      }
    } else {
      onChange(value.filter((iso) => iso !== startIso));
    }
  };

  const handleChange = (oldIso: TDateISO, oldIsoEnd?: TDateISO) => {
    return (newIso: TDateISO, newIsoEnd?: TDateISO) => {
      if (isWindow) {
        const startIndex = value.indexOf(oldIso as TDateISO);
        if (startIndex !== -1) {
          const updated = [...value];
          updated[startIndex] = newIso;
          if (newIsoEnd) {
            updated[startIndex + 1] = newIsoEnd;
          }
          onChange(updated);
        }
      } else {
        const updated = value.map((iso) => (iso === oldIso ? newIso : iso));
        onChange(updated);
      }
    };
  };

  const getCardsData = () => {
    if (isWindow) {
      return getWindows().map((window) => ({
        iso: window.start,
        isoEnd: window.end,
        index: window.index,
      }));
    }
    return value.map((iso, index) => ({
      iso,
      index,
    }));
  };

  const cards = getCardsData();

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 1, mb: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                mb: 0.25,
                color: "var(--fx-surface-text, #0F172A)",
              }}
            >
              {label}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "var(--fx-surface-textsecondary, #64748B)", mb: 1 }}>{description[type]}</Typography>
          </Box>
          {headerAction && (multiplesDateTimes || value.length === 0) ? <Box sx={{ flexShrink: 0, mt: "auto" }}>{headerAction}</Box> : null}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            gap: 1,
            width: "100%",
            minWidth: 0,
          }}
        >
          {cards.length === 0 ? (
            <Box
              sx={{
                border: "1px dashed var(--fx-surface-borderstrong, #CBD5E1)",
                borderRadius: 2,
                p: 2,
                textAlign: "center",
                color: "var(--fx-surface-texttertiary, #94A3B8)",
                fontSize: 14,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                maxWidth: 400,
                bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
              }}
            >
              <AccessTime fontSize="small" color="disabled" />
              <Typography sx={{ fontSize: 12, color: "var(--fx-surface-textsecondary, #64748B)", whiteSpace: "nowrap" }}>{noArrivalDateTimeAddedLabel}</Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: !isWindow ? "repeat(2, 1fr)" : "1fr",
                  md: !isWindow ? "repeat(3, 1fr)" : "repeat(2, 1fr)",
                },
                gap: { xs: 0.75, sm: 1 },
                width: "100%",
                minWidth: 0,
                maxWidth: "100%",
                gridAutoRows: "min-content",
              }}
              key="arrival-date-time-cards-container"
            >
              {cards.map((card) => (
                <ArrivalDateTimeCard
                  isDisabled={isDisabled}
                  key={`${card.iso}-${card.isoEnd}`}
                  iso={card.iso}
                  isoEnd={card.isoEnd}
                  onChange={handleChange(card.iso, card.isoEnd)}
                  onDelete={() => handleDelete(card.iso)}
                  index={card.index}
                  dateEnabled={dateEnabled}
                  type={type}
                  mode={mode}
                  handleRepetitiveStartDateChange={handleRepetitiveStartDateChange}
                  minuteStep={minuteStep}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </LocalizationProvider>
  );
};
